import axios from 'axios';
import { MeApiService } from '../../common/services/me.api.service';
import { StocksApiService } from '../../common/services/stocks.api.service';
import { UserSessionsApiService } from '../../common/services/user-sessions.api.service';
import { UserTokensApiService } from '../../common/services/user-tokens.api.service';
import REQUEST_STATUS from '../../common/enums/requestStatus';
import { UserModel } from '../../common/models/user.model';

export default {
  namespaced: true,
  state: {
    accessToken: null,
    refreshToken: null,
    status: null,
    tokensRequest: null,
    tokenTimer: null,
    user: null,
    userPermissions: null,
    userRoles: [],
    session: null,
  },
  getters: {
    isAuthenticated: state => !!state.accessToken,
    authStatus: state => state.status,
    getUser: state => state.user,
    getUserRoles: state => state.userRoles,
    getTokensRequest: state => state.tokensRequest,
    getPermissions: state => state.userPermissions,
    getRefreshToken: state => state.refreshToken,
    getSession: state => state.session,
  },
  mutations: {
    AUTH_REQUEST(state) {
      state.status = REQUEST_STATUS.LOADING;
    },
    AUTH_SUCCESS(state, tokens) {
      state.status = REQUEST_STATUS.SUCCESS;
      state.accessToken = tokens.accessToken;
      state.refreshToken = tokens.refreshToken;
      state.tokenTimer = tokens.tokenTimer;
    },
    AUTH_ERROR(state) {
      state.status = REQUEST_STATUS.FAIL;
    },
    SET_TOKENS_REQUEST(state, payload) {
      state.tokensRequest = payload;
    },
    AUTH_LOGOUT(state) {
      state.status = null;
      state.accessToken = null;
    },
    SET_USER(state, payload) {
      state.user = payload;
    },
    SET_SESSION(state, payload) {
      state.session = payload;
    },
    SET_USER_ROLES(state, payload) {
      state.userRoles = payload;
    },
    SET_USER_PERMISSIONS(state, payload) {
      state.userPermissions = payload;
    },
    CLEAR_TIMER(state) {
      clearInterval(state.tokenTimer);
      state.tokenTimer = null;
    },
  },
  actions: {
    attemptLogin({ commit, dispatch }, data) {
      commit('AUTH_REQUEST');

      return new Promise((resolve, reject) => {
        UserSessionsApiService.post(data)
          .then(response => {
            dispatch('setUpSession', { session: response.getItem() });
            resolve(response);
          })
          .catch(error => {
            reject(error);
            dispatch('handleError', error);
          });
      });
    },

    refreshSession({ commit, dispatch }, { refreshToken }) {
      commit('AUTH_REQUEST');
      commit('CLEAR_TIMER');

      const userTokensRequest = UserTokensApiService.post({ refresh_token: refreshToken })
        .then(response => {
          const session = response.getItem();

          return dispatch('setUpSession', { session: session });
        })
        .catch(() => {
          commit('AUTH_LOGOUT');
          commit('SET_USER', null);
        });

      commit('SET_TOKENS_REQUEST', userTokensRequest);
      return userTokensRequest;
    },

    logout({ commit }) {
      commit('AUTH_LOGOUT');
      commit('SET_USER', null);
      localStorage.removeItem(process.env.VUE_APP_JWT_BEARER);
      localStorage.removeItem(process.env.VUE_APP_JWT_REFRESH);
      commit('CLEAR_TIMER');
      delete axios.defaults.headers.common.Authorization;
      return Promise.resolve();
    },

    fetchMe({ commit, dispatch }) {
      return MeApiService.get(builder => {
        builder.embed('user_roles');
      })
        .then(response => {
          const me = response.getItem();
          commit('SET_USER', new UserModel(me.user));
          commit('SET_USER_ROLES', me.embeds.user_roles);
          commit('SET_USER_PERMISSIONS', me.permissions);

          if (Object.prototype.hasOwnProperty.call(me.permissions, 'settings')) {
            dispatch('settings/fetchAll', null, { root: true });
          }

          StocksApiService.get(me.firstAllowedStock).then(response => {
            let stock;
            if (me.firstAllowedStock) {
              stock = response.getItem();
            } else {
              stock = response.getItems()[0];
            }

            dispatch('users/setStoreData', stock, { root: true });
          });

          return Promise.resolve();
        })
        .catch(error => {
          dispatch('handleError', error);
          return Promise.reject();
        });
    },

    setUpSession({ commit, dispatch, getters }, { session }) {
      axios.defaults.headers.common.Authorization = session.accessToken;
      localStorage.setItem(process.env.VUE_APP_JWT_BEARER, session.accessToken);
      localStorage.setItem(process.env.VUE_APP_JWT_REFRESH, session.refreshToken);

      const tokensPayload = {
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        tokenTimer: setInterval(() => {
          if (new Date(session.accessExpirationAt) > Date.now() + 30000) {
            return;
          }
          dispatch('refreshSession', { refreshToken: getters.getRefreshToken });
        }, 1000),
      };

      commit('SET_SESSION', session);
      commit('AUTH_SUCCESS', tokensPayload);
      return Promise.resolve();
    },

    handleError({ commit }, response) {
      commit('AUTH_ERROR');
      const message = response.getFirstValidationErrorMessage() || response.getMessage();
      if (message) {
        this.$app.$alertError(message);
      }
    },
  },
};
